import React, { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../api/client';
import { useAuth } from '../contexts/AuthContext';

interface KnowledgeEntry {
  id: string;
  question: string;
  answer: string;
  category: string;
  status: string;
  tags: string[];
  isFaq: boolean;
  viewCount: number;
  updatedAt: string;
}

interface CategoryCount {
  category: string;
  count: number;
}

interface SearchResult {
  id: string;
  question: string;
  answer: string;
  category: string;
  tags: string[];
  relevanceScore: number;
  isBestMatch: boolean;
  isFaq: boolean;
  updatedAt: string;
}

interface SearchResponse {
  results: SearchResult[];
  totalCount: number;
  query: string;
  cached: boolean;
  suggestion?: string;
}

const CATEGORIES = ['Events', 'Registration', 'Logistics', 'Cultural', 'Accommodations', 'Subcommittees', 'General'];

const styles: Record<string, React.CSSProperties> = {
  page: { padding: '2rem', maxWidth: '1100px', margin: '0 auto' },
  title: { color: '#1a6b3c', fontSize: '1.75rem', margin: '0 0 0.5rem 0' },
  subtitle: { color: '#666', fontSize: '0.95rem', marginBottom: '1.5rem' },
  searchBar: {
    display: 'flex',
    gap: '0.5rem',
    marginBottom: '1.5rem',
  },
  searchInput: {
    flex: 1,
    padding: '0.6rem 1rem',
    borderRadius: '6px',
    border: '1px solid #ddd',
    fontSize: '0.95rem',
    outline: 'none',
  },
  searchBtn: {
    padding: '0.6rem 1.25rem',
    borderRadius: '6px',
    border: 'none',
    backgroundColor: '#1a6b3c',
    color: '#fff',
    fontSize: '0.9rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
  tabs: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '0.5rem',
    marginBottom: '1.5rem',
  },
  tab: {
    padding: '0.4rem 0.9rem',
    borderRadius: '20px',
    border: '1px solid #ddd',
    backgroundColor: '#fff',
    fontSize: '0.85rem',
    cursor: 'pointer',
    transition: 'all 0.15s',
  },
  tabActive: {
    padding: '0.4rem 0.9rem',
    borderRadius: '20px',
    border: '1px solid #1a6b3c',
    backgroundColor: '#1a6b3c',
    color: '#fff',
    fontSize: '0.85rem',
    cursor: 'pointer',
    fontWeight: 600,
  },
  viewToggle: {
    display: 'flex',
    gap: '0.5rem',
    marginBottom: '1rem',
  },
  toggleBtn: {
    padding: '0.35rem 0.75rem',
    borderRadius: '4px',
    border: '1px solid #ddd',
    backgroundColor: '#fff',
    fontSize: '0.8rem',
    cursor: 'pointer',
  },
  toggleBtnActive: {
    padding: '0.35rem 0.75rem',
    borderRadius: '4px',
    border: '1px solid #1a6b3c',
    backgroundColor: '#e8f5e9',
    fontSize: '0.8rem',
    cursor: 'pointer',
    fontWeight: 600,
    color: '#1a6b3c',
  },
  cardGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    gap: '1rem',
    marginBottom: '2rem',
  },
  card: {
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    padding: '1rem',
    backgroundColor: '#fff',
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
  },
  cardQuestion: {
    fontSize: '0.95rem',
    fontWeight: 600,
    color: '#1a6b3c',
    margin: '0 0 0.5rem 0',
  },
  cardAnswer: {
    fontSize: '0.85rem',
    color: '#555',
    margin: '0 0 0.75rem 0',
    lineHeight: 1.4,
  },
  badge: {
    display: 'inline-block',
    padding: '0.2rem 0.5rem',
    borderRadius: '12px',
    backgroundColor: '#e8f5e9',
    color: '#1a6b3c',
    fontSize: '0.75rem',
    fontWeight: 500,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    marginBottom: '2rem',
  },
  th: {
    textAlign: 'left' as const,
    padding: '0.6rem 0.75rem',
    borderBottom: '2px solid #e0e0e0',
    fontSize: '0.8rem',
    fontWeight: 600,
    color: '#666',
    textTransform: 'uppercase' as const,
  },
  td: {
    padding: '0.6rem 0.75rem',
    borderBottom: '1px solid #f0f0f0',
    fontSize: '0.85rem',
    color: '#333',
  },
  faqSection: {
    marginTop: '2rem',
    borderTop: '2px solid #e0e0e0',
    paddingTop: '1.5rem',
  },
  faqTitle: {
    fontSize: '1.25rem',
    color: '#1a6b3c',
    margin: '0 0 1rem 0',
  },
  faqCategory: {
    marginBottom: '1.25rem',
  },
  faqCategoryTitle: {
    fontSize: '0.95rem',
    fontWeight: 600,
    color: '#333',
    margin: '0 0 0.5rem 0',
  },
  faqItem: {
    padding: '0.5rem 0.75rem',
    borderLeft: '3px solid #1a6b3c',
    marginBottom: '0.5rem',
    backgroundColor: '#f9fafb',
    borderRadius: '0 4px 4px 0',
  },
  faqQuestion: {
    fontSize: '0.85rem',
    fontWeight: 500,
    color: '#333',
    margin: 0,
    cursor: 'pointer',
  },
  faqAnswer: {
    fontSize: '0.8rem',
    color: '#555',
    margin: '0.35rem 0 0 0',
    lineHeight: 1.4,
  },
  showMore: {
    background: 'none',
    border: 'none',
    color: '#1a6b3c',
    fontSize: '0.8rem',
    cursor: 'pointer',
    padding: '0.25rem 0',
    fontWeight: 500,
  },
  empty: {
    textAlign: 'center' as const,
    padding: '2rem',
    color: '#999',
    fontSize: '0.9rem',
  },
  searchResults: {
    marginBottom: '1.5rem',
    padding: '1rem',
    backgroundColor: '#f9fafb',
    borderRadius: '8px',
    border: '1px solid #e0e0e0',
  },
  searchResultItem: {
    padding: '0.75rem',
    borderBottom: '1px solid #eee',
    cursor: 'pointer',
  },
  bestMatch: {
    backgroundColor: '#e8f5e9',
    borderLeft: '3px solid #1a6b3c',
    paddingLeft: '0.75rem',
  },
};

export function KnowledgePage() {
  const { user } = useAuth();
  const [categories, setCategories] = useState<CategoryCount[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [entries, setEntries] = useState<KnowledgeEntry[]>([]);
  const [viewMode, setViewMode] = useState<'card' | 'table'>('card');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResponse | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [faqEntries, setFaqEntries] = useState<KnowledgeEntry[]>([]);
  const [expandedFaqCategories, setExpandedFaqCategories] = useState<Set<string>>(new Set());
  const [expandedFaqItem, setExpandedFaqItem] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [helpExpanded, setHelpExpanded] = useState(false);

  // Fetch categories
  useEffect(() => {
    apiClient<Record<string, number>>('/knowledge/categories')
      .then((data) => {
        const cats: CategoryCount[] = Object.entries(data).map(([category, count]) => ({ category, count }));
        setCategories(cats);
      })
      .catch(() => setCategories([]));
  }, []);

  // Fetch entries when category changes
  useEffect(() => {
    setLoading(true);
    const params = selectedCategory ? `?category=${selectedCategory}&status=Published` : '?status=Published';
    apiClient<{ entries: KnowledgeEntry[]; total: number }>(`/knowledge/entries${params}`)
      .then((res) => {
        setEntries(res.entries || []);
        setLoading(false);
      })
      .catch(() => {
        setEntries([]);
        setLoading(false);
      });
  }, [selectedCategory]);

  // Fetch FAQ entries
  useEffect(() => {
    apiClient<{ entries: KnowledgeEntry[]; total: number }>('/knowledge/entries?status=Published')
      .then((res) => {
        const faqs = (res.entries || []).filter((e) => e.isFaq);
        setFaqEntries(faqs);
      })
      .catch(() => setFaqEntries([]));
  }, []);

  const handleSearch = useCallback(async () => {
    if (!searchQuery.trim()) {
      setSearchResults(null);
      return;
    }
    setIsSearching(true);
    try {
      const result = await apiClient<SearchResponse>('/knowledge/search', {
        method: 'POST',
        body: {
          query: searchQuery.trim(),
          context: {
            userRole: user?.role || 'Attendee',
            currentPage: 'knowledge',
          },
        },
      });
      setSearchResults(result);
    } catch {
      setSearchResults(null);
    } finally {
      setIsSearching(false);
    }
  }, [searchQuery, user]);

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  const truncateAnswer = (answer: string, maxLen = 150) => {
    if (answer.length <= maxLen) return answer;
    return answer.slice(0, maxLen) + '...';
  };

  // Group FAQ entries by category
  const faqByCategory = faqEntries.reduce<Record<string, KnowledgeEntry[]>>((acc, entry) => {
    if (!acc[entry.category]) acc[entry.category] = [];
    acc[entry.category].push(entry);
    return acc;
  }, {});

  // Sort each group by viewCount descending
  Object.values(faqByCategory).forEach((group) => {
    group.sort((a, b) => b.viewCount - a.viewCount);
  });

  return (
    <div style={styles.page}>
      <h1 style={styles.title}>Knowledge Base</h1>
      <p style={styles.subtitle}>Browse and search our knowledge base for answers to common questions.</p>

      {/* How to Use Help Section */}
      <div style={{ marginBottom: '1.25rem' }}>
        <span
          onClick={() => setHelpExpanded(!helpExpanded)}
          style={{
            color: '#1a6b3c',
            fontSize: '0.9rem',
            cursor: 'pointer',
            fontWeight: 500,
            userSelect: 'none',
          }}
        >
          {helpExpanded ? '▾' : '▸'} ℹ️ How to use the Knowledge Base
        </span>
        {helpExpanded && (
          <div
            style={{
              marginTop: '0.75rem',
              padding: '1rem 1.25rem',
              backgroundColor: '#f0f9f4',
              border: '1px solid #c8e6c9',
              borderRadius: '8px',
              fontSize: '0.85rem',
              lineHeight: 1.7,
              color: '#333',
            }}
          >
            <div style={{ marginBottom: '0.4rem' }}>🔍 <strong>Quick Search:</strong> Click the blue search button (bottom-right) from any page</div>
            <div style={{ marginBottom: '0.4rem' }}>📂 <strong>Browse:</strong> Use category tabs below to filter by topic</div>
            <div style={{ marginBottom: '0.4rem' }}>🃏/📋 <strong>Views:</strong> Toggle between card and table view</div>
            <div style={{ marginBottom: '0.4rem' }}>⭐ <strong>FAQ:</strong> Scroll down for frequently asked questions</div>
            <div>💬 <strong>Contribute:</strong> Use the AI Concierge chatbot and save helpful answers</div>
          </div>
        )}
      </div>

      {/* Search Bar */}
      <div style={styles.searchBar}>
        <input
          style={styles.searchInput}
          type="text"
          placeholder="Search the knowledge base..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyDown={handleSearchKeyDown}
        />
        <button style={styles.searchBtn} onClick={handleSearch} disabled={isSearching}>
          {isSearching ? '...' : '🔍 Search'}
        </button>
      </div>

      {/* Search Results */}
      {searchResults && (
        <div style={styles.searchResults}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
            <strong style={{ fontSize: '0.9rem' }}>
              {searchResults.totalCount} result{searchResults.totalCount !== 1 ? 's' : ''} for "{searchResults.query}"
            </strong>
            <button
              style={{ ...styles.showMore, fontSize: '0.8rem' }}
              onClick={() => setSearchResults(null)}
            >
              ✕ Clear
            </button>
          </div>
          {searchResults.suggestion && (
            <p style={{ color: '#666', fontSize: '0.85rem', margin: '0 0 0.5rem 0' }}>
              💡 {searchResults.suggestion}
            </p>
          )}
          {searchResults.results.map((r) => (
            <div
              key={r.id}
              style={{ ...styles.searchResultItem, ...(r.isBestMatch ? styles.bestMatch : {}) }}
            >
              {r.isBestMatch && <span style={{ fontSize: '0.7rem', color: '#1a6b3c', fontWeight: 600 }}>⭐ Best Match</span>}
              <p style={{ margin: '0.25rem 0', fontWeight: 500, fontSize: '0.9rem' }}>{r.question}</p>
              <p style={{ margin: '0.25rem 0', fontSize: '0.8rem', color: '#555' }}>{truncateAnswer(r.answer)}</p>
              <span style={styles.badge}>{r.category}</span>
            </div>
          ))}
        </div>
      )}

      {/* Category Tabs */}
      <div style={styles.tabs}>
        <button
          style={selectedCategory === null ? styles.tabActive : styles.tab}
          onClick={() => setSelectedCategory(null)}
        >
          All ({categories.reduce((sum, c) => sum + c.count, 0)})
        </button>
        {CATEGORIES.map((cat) => {
          const catData = categories.find((c) => c.category === cat);
          const count = catData?.count || 0;
          return (
            <button
              key={cat}
              style={selectedCategory === cat ? styles.tabActive : styles.tab}
              onClick={() => setSelectedCategory(cat)}
            >
              {cat} ({count})
            </button>
          );
        })}
      </div>

      {/* View Toggle */}
      <div style={styles.viewToggle}>
        <button
          style={viewMode === 'card' ? styles.toggleBtnActive : styles.toggleBtn}
          onClick={() => setViewMode('card')}
        >
          🃏 Cards
        </button>
        <button
          style={viewMode === 'table' ? styles.toggleBtnActive : styles.toggleBtn}
          onClick={() => setViewMode('table')}
        >
          📋 Table
        </button>
      </div>

      {/* Entries */}
      {loading ? (
        <p style={styles.empty}>Loading...</p>
      ) : entries.length === 0 ? (
        <p style={styles.empty}>No entries found{selectedCategory ? ` in ${selectedCategory}` : ''}.</p>
      ) : viewMode === 'card' ? (
        <div style={styles.cardGrid}>
          {entries.map((entry) => (
            <div key={entry.id} style={styles.card}>
              <p style={styles.cardQuestion}>{entry.question}</p>
              <p style={styles.cardAnswer}>{truncateAnswer(entry.answer)}</p>
              <span style={styles.badge}>{entry.category}</span>
            </div>
          ))}
        </div>
      ) : (
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Question</th>
              <th style={styles.th}>Category</th>
              <th style={styles.th}>Tags</th>
              <th style={styles.th}>Updated</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((entry) => (
              <tr key={entry.id}>
                <td style={styles.td}>{entry.question}</td>
                <td style={styles.td}><span style={styles.badge}>{entry.category}</span></td>
                <td style={styles.td}>{entry.tags.join(', ')}</td>
                <td style={styles.td}>{new Date(entry.updatedAt).toLocaleDateString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* FAQ Section */}
      {Object.keys(faqByCategory).length > 0 && (
        <div style={styles.faqSection}>
          <h2 style={styles.faqTitle}>📋 Frequently Asked Questions</h2>
          {Object.entries(faqByCategory).map(([category, items]) => {
            const isExpanded = expandedFaqCategories.has(category);
            const displayItems = isExpanded ? items : items.slice(0, 5);
            return (
              <div key={category} style={styles.faqCategory}>
                <h3 style={styles.faqCategoryTitle}>{category}</h3>
                {displayItems.map((item) => (
                  <div key={item.id} style={styles.faqItem}>
                    <p
                      style={styles.faqQuestion}
                      onClick={() => setExpandedFaqItem(expandedFaqItem === item.id ? null : item.id)}
                    >
                      {expandedFaqItem === item.id ? '▾' : '▸'} {item.question}
                    </p>
                    {expandedFaqItem === item.id && (
                      <p style={styles.faqAnswer}>{item.answer}</p>
                    )}
                  </div>
                ))}
                {items.length > 5 && !isExpanded && (
                  <button
                    style={styles.showMore}
                    onClick={() => setExpandedFaqCategories((prev) => new Set([...prev, category]))}
                  >
                    Show more ({items.length - 5} more)
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
